import { debounce, unescape as ldUnescape, reject, uniqBy } from 'lodash-es'
import { mapActions, mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

import DraftCloser from 'src/components/draft_closer/draft_closer.vue'
import EmojiInput from 'src/components/emoji_input/emoji_input.vue'
import suggestor from 'src/components/emoji_input/suggestor.js'
import LanguageMenu from 'src/components/language_menu/language_menu.vue'
import MediaUpload from 'src/components/media_upload/media_upload.vue'
import PostAttachments from 'src/components/post_attachments/post_attachments.vue'
import PostToolbar from 'src/components/post_toolbar/post_toolbar.vue'
import { focusAtEnd } from 'src/components/rich_textarea/rich_textarea.js'
import RichTextarea from 'src/components/rich_textarea/rich_textarea.vue'
import ScopeMenu from 'src/components/scope_menu/scope_menu.vue'
import StatusContent from 'src/components/status_content/status_content.vue'
import { propsToNative } from '../../services/attributes_helper/attributes_helper.service.js'
import statusPoster from '../../services/status_poster/status_poster.service.js'

import { useDraftsStore } from 'src/stores/drafts.js'
import { useEmojiStore } from 'src/stores/emoji.js'
import { useInstanceStore } from 'src/stores/instance.js'
import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useInterfaceStore } from 'src/stores/interface.js'
import { useMediaViewerStore } from 'src/stores/media_viewer.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useUsersStore } from 'src/stores/users.js'

import {
  DEFAULT_EXPIRY,
  EXPIRY_CHOICES,
  expiryLabel,
} from 'src/services/expiry/expiry.js'
import { pollFormToMasto } from 'src/services/poll/poll.service.js'
import {
  draftStep,
  findDraft,
  hasContent,
  loadDraft,
} from 'src/services/post_draft/post_draft.js'
import {
  defaultSchedule,
  earliestSchedule,
  formatScheduleTime,
  scheduleProblem,
  scheduleProblemKey,
} from 'src/services/scheduling/scheduling.js'

const PostStatusForm = {
  props: {
    // Status editing stuff
    statusId: String,
    statusText: String,
    statusSubject: String,
    statusIsSensitive: {
      type: Boolean,
      required: false,
      default: null, // Avoiding automatic conversion null -> false
    },
    statusPoll: Object,
    statusQuote: Object,
    statusFiles: Array,
    statusMediaDescriptions: Object,
    statusVisibility: String,
    statusContentType: String,

    // Replies/mentions
    repliedStatus: Object, // Object of a status replying to
    // opened by the Quote post action: a quote of repliedStatus, not a
    // reply; switches when Reply or Quote is pressed while the form is open
    quoting: Boolean,
    profileMention: Object, // Mentioned user (used in profile page -> mention)

    // Draft stuff
    hideDraft: Boolean, // Disable drafts functionality
    closeable: Boolean, // Whether form can be closed (i.e. in replies)
    draftId: String, // ID of the draft to be used

    // Chats stuff
    chatView: Boolean, // Used for the "submit on enter" user setting
    maxHeight: Number,
    placeholder: String,
    postHandler: Function, // Used to override poster to use chats one instead of status one
    preserveFocus: Boolean, // Keep focus on form after posting
    autoFocus: Boolean, // Steal focus when form is opened
    fileLimit: Number, // Chats only support 1 attachment :(
    emojiPickerPlacement: String,
    optimisticPosting: Boolean, // Don't wait for confirmation that post is done

    // Feature toggles for special cases (mostly chats)
    disableSubject: Boolean,
    disableScopeSelector: Boolean,
    disableVisibilitySelector: Boolean,
    disablePolls: Boolean,
    disableSensitivityCheckbox: Boolean,
    disableSubmit: Boolean,
    disablePreview: Boolean,
    disableDraft: Boolean,
  },
  emits: [
    'posted',
    'draft-done',
    'resize',
    'mediaplay',
    'mediapause',
    'close-accepted',
    'update',
  ],
  data() {
    return {
      initialized: false,
      // Posting stuff
      idempotencyKey: '',
      /* Data is initialized first, but we have no access to .computed yet
       * which we need for some defaults (i.e. user configration)
       * so we pre-fill with stuff meant for status editing and later
       * back-fill with defaults in .created()
       */
      newStatus: {
        status: this.statusText ?? null,
        spoilerText: this.statusSubject ?? null,
        quote: this.statusQuote ?? null,
        files: this.statusFiles ?? null,
        poll: this.statusPoll ?? null,
        mediaDescriptions: this.statusMediaDescriptions ?? null,
        nsfw: this.statusIsSensitive ?? null,
        visibility: this.statusVisibility ?? null,
        contentType: this.statusContentType ?? null,
        // datetime-local value while the post is to be scheduled
        scheduledAt: null,
        // seconds until a self-deleting post goes away
        expiresIn: null,
        // ISO 639 code of the post's language; null leaves it to the
        // server, which detects it
        language: null,
      },

      // Attachments
      dropFiles: [],
      uploadingFiles: false,

      // Preview
      preview: null,
      previewLoading: false,

      // Draft
      saveInhibited: true,
      saveable: false,

      // Misc States
      subjectOpen: false,
      emojiInputShown: false,
      error: null,
      posting: false,
    }
  },
  components: {
    MediaUpload,
    EmojiInput,
    PollForm: defineAsyncComponent(
      () => import('src/components/poll/poll_form.vue'),
    ),
    Quote: defineAsyncComponent(() => import('src/components/quote/quote.vue')),
    RichTextarea,
    ScopeMenu,
    LanguageMenu,
    StatusContent,
    PostAttachments,
    DraftCloser,
    PostToolbar,
  },
  created() {
    this.updateIdempotencyKey()

    const draft = findDraft(useDraftsStore().drafts, {
      draftId: this.draftId,
      type: this.statusType,
      refId: this.refId,
      disabled: this.disableDraft,
    })

    if (draft) {
      Object.assign(this.newStatus, loadDraft(this.newStatus, draft))
      // one draft per post, reply or quote: the button that opened the form
      // decides which it is now
      if (this.repliedStatus && !!draft.quote?.thread !== this.quoting) {
        this.setQuoteMode(this.quoting)
      }
    } else {
      Object.entries(this.defaultNewStatus).forEach(([key, value]) => {
        this.newStatus[key] = this.newStatus[key] ?? value
      })
    }

    this.subjectOpen = !!this.newStatus.spoilerText
    this.initialized = true
  },
  mounted() {
    // chats and statuses keep their scroll when the form changes height
    this.resizeObserver = new ResizeObserver(() => this.$emit('resize'))
    this.resizeObserver.observe(this.$el)

    if (this.repliedStatus) {
      const textLength = this.textareaEl().value.length
      this.textareaEl().setSelectionRange(textLength, textLength)
    }

    if (this.repliedStatus || this.autoFocus) {
      this.textareaEl().focus()
    }
  },
  computed: {
    // Visibility / expansion state of subcomponents
    pollFormVisible() {
      return this.hasPoll
    },
    showPreview() {
      return !this.disablePreview && (!!this.preview || this.previewLoading)
    },
    showSubject() {
      return !this.disableSubject && this.subjectOpen
    },
    // Preview, the format and the draft actions share the menu beside Post
    formatChoice() {
      return !this.disableScopeSelector && this.postFormats.length > 1
    },
    // preview, the format and drafts, in the menu beside Post
    postMenuItems() {
      return [
        {
          key: 'preview',
          label: this.$t('post_status.preview'),
          icon: 'eye',
          checked: this.showPreview,
          mark: false,
          hidden: this.disablePreview,
          action: this.togglePreview,
        },
        { key: 'd1', divider: true },
        {
          key: 'format',
          heading: this.$t('post_status.content_type_selection'),
          hidden: !this.formatChoice,
        },
        ...this.postFormats.map((format) => ({
          key: format,
          label: this.$t(`post_status.content_type["${format}"]`),
          radio: true,
          checked: this.newStatus.contentType === format,
          hidden: !this.formatChoice,
          action: () => {
            this.newStatus.contentType = format
          },
        })),
        { key: 'd2', divider: true },
        {
          key: 'schedule',
          label: this.$t('post_status.schedule'),
          icon: 'clock',
          checked: this.newStatus.scheduledAt !== null,
          mark: false,
          hidden: !this.newPostOptions,
          action: this.toggleSchedule,
        },
        {
          key: 'expire',
          label: this.$t('post_status.expire'),
          icon: 'hourglass-half',
          checked: this.newStatus.expiresIn !== null,
          mark: false,
          hidden: !this.newPostOptions,
          action: this.toggleExpiry,
        },
        {
          key: 'draft',
          label: this.$t(
            this.closeable
              ? 'post_status.save_to_drafts_and_close_button'
              : 'post_status.save_to_drafts_button',
          ),
          icon: 'file-lines',
          disabled: !this.safeToSaveDraft,
          hidden: this.hideExtraActions,
          action: this.saveDraft,
        },
      ]
    },
    showPostMenu() {
      return !this.disablePreview || this.formatChoice || !this.hideExtraActions
    },

    // Composing stuff
    statusType() {
      if (this.repliedStatus) {
        return 'reply'
      } else if (this.profileMention) {
        return 'mention'
      } else if (this.statusId) {
        return 'edit'
      } else {
        return 'new'
      }
    },
    refId() {
      if (this.repliedStatus) {
        return this.repliedStatus.id
      } else if (this.profileMention) {
        return this.profileMention.id
      } else if (this.statusId) {
        return this.statusId
      } else {
        return null
      }
    },
    mentionsString() {
      if (this.statusType !== 'reply' && this.statusType !== 'mention')
        return ''
      let allAttentions = [...(this.repliedStatus?.attentions || [])]
      const repliedUser = this.repliedStatus?.user || this.profileMention

      if (repliedUser) allAttentions.unshift(repliedUser)

      allAttentions = uniqBy(allAttentions, 'id')
      allAttentions = reject(allAttentions, { id: this.currentUser.id })

      const mentions = allAttentions.map(
        (attention) => `@${attention.screen_name}`,
      )

      return mentions.length > 0 ? mentions.join(' ') + ' ' : ''
    },
    defaultNewStatus() {
      const defaultNewStatus = {
        files: [],
        poll: null,
        quote: null,
        mediaDescriptions: {},
      }

      const scope = (() => {
        if (this.repliedStatus) {
          if (this.repliedStatus.visibility === 'direct') return 'direct'
          if (this.userDefaultScopeCopy) return this.repliedStatus.visibility
        }
        return this.userDefaultScope
      })()

      const preset = this.$route.query.message
      const statusText = preset ?? ''

      // a quote is a post of its own: no reply mention, no reply subject
      const quoting = this.quoting && this.repliedStatus
      defaultNewStatus.status = quoting
        ? statusText
        : this.mentionsString + statusText
      defaultNewStatus.spoilerText = quoting
        ? ''
        : (this.repliedSubjectString ?? '')
      if (quoting) {
        defaultNewStatus.quote = { thread: true, id: this.repliedStatus.id }
      }
      defaultNewStatus.nsfw = this.userDefaultSensitive
      defaultNewStatus.visibility = scope
      defaultNewStatus.contentType = this.userDefaultPostContentType

      return defaultNewStatus
    },
    // -Edit
    isEdit() {
      return this.statusId !== undefined && this.statusId.trim() !== ''
    },
    // -Reply
    inReplyToStatusId() {
      return !this.hasQuote ||
        !this.newStatus.quote.thread ||
        !this.newStatus.quote.id
        ? this.repliedStatus?.id
        : undefined
    },
    repliedSubjectString() {
      if (!this.repliedStatus?.summary) return null
      const decodedSummary = ldUnescape(this.repliedStatus.summary)
      const behavior = this.mergedConfig.subjectLineBehavior
      const startsWithRe = decodedSummary.match(/^re[: ]/i)
      if ((behavior !== 'noop' && startsWithRe) || behavior === 'masto') {
        return decodedSummary
      } else if (behavior === 'email') {
        return 're: '.concat(decodedSummary)
      } else if (behavior === 'noop') {
        return ''
      }
    },
    // -Poll
    hasPoll() {
      return this.newStatus.poll != null
    },
    // -Quotes
    hasQuote() {
      return this.newStatus.quote !== null
    },
    // only a quote of the replied-to post: a link quote in an older draft
    // is not posted unseen
    quoteId() {
      return (this.newStatus.quote?.thread && this.newStatus.quote.id) || null
    },
    // a quote of the replied-to post (the Quote post action), not a reply
    quoteThreadToggled: {
      get() {
        return this.newStatus.quote?.thread
      },
      set(value) {
        if (value) {
          this.newStatus.quote = {}
          this.newStatus.quote.thread = true
          this.newStatus.quote.id = this.repliedStatus.id
        } else {
          this.newStatus.quote = null
        }
      },
    },
    // scheduling and self-deleting: for new posts and replies, not for
    // edits and chats
    newPostOptions() {
      return !this.isEdit && !this.postHandler
    },
    // a time is set for this post
    scheduling() {
      return this.newPostOptions && this.newStatus.scheduledAt !== null
    },
    scheduledAtIso() {
      const { scheduledAt } = this.newStatus
      if (!this.scheduling || !scheduledAt) return undefined
      const time = new Date(scheduledAt)
      return Number.isNaN(time.getTime()) ? undefined : time.toISOString()
    },
    earliestSchedule() {
      return earliestSchedule()
    },
    // a preview must never create a scheduled post
    previewOptions() {
      return {
        ...this.postingOptions,
        media: [],
        poll: null,
        preview: true,
        scheduledAt: undefined,
        expiresIn: undefined,
      }
    },
    postingOptions() {
      const poll = this.hasPoll ? pollFormToMasto(this.newStatus.poll) : null

      return {
        status: this.newStatus.status,
        spoilerText: this.newStatus.spoilerText,
        visibility: this.newStatus.visibility,
        sensitive: this.newStatus.nsfw,
        media: this.newStatus.files,
        inReplyToStatusId: this.inReplyToStatusId,
        quoteId: this.quoteId,
        contentType: this.newStatus.contentType,
        poll,
        idempotencyKey: this.idempotencyKey,
        scheduledAt: this.scheduledAtIso,
        expiresIn: this.expiring ? this.newStatus.expiresIn : undefined,
        language: this.newPostOptions
          ? (this.newStatus.language ?? undefined)
          : undefined,
      }
    },
    expiring() {
      return this.newPostOptions && this.newStatus.expiresIn !== null
    },
    expiryChoices() {
      return EXPIRY_CHOICES.map((seconds) => ({
        seconds,
        label: expiryLabel(seconds, this.$i18n?.locale),
      }))
    },

    // Emoji stuff
    emojiUserSuggestor() {
      return suggestor({
        emoji: [
          ...useEmojiStore().standardEmojiList,
          ...useEmojiStore().customEmoji,
        ],
      })
    },
    emojiSuggestor() {
      return suggestor({
        emoji: [
          ...useEmojiStore().standardEmojiList,
          ...useEmojiStore().customEmoji,
        ],
      })
    },
    emoji() {
      return useEmojiStore().standardEmojiList
    },
    customEmoji() {
      return useEmojiStore().customEmoji
    },

    // Length & Limits
    statusLength() {
      return this.newStatus.status.length
    },
    spoilerTextLength() {
      return this.newStatus.spoilerText.length
    },
    statusLengthLimit() {
      return useInstanceStore().limits.textLimit
    },
    hasStatusLengthLimit() {
      return this.statusLengthLimit > 0
    },
    charactersLeft() {
      return (
        this.statusLengthLimit - (this.statusLength + this.spoilerTextLength)
      )
    },
    showCounter() {
      return this.hasStatusLengthLimit && this.charactersLeft <= 500
    },
    isOverLengthLimit() {
      return this.hasStatusLengthLimit && this.charactersLeft < 0
    },
    isEmptyStatus() {
      return (
        this.newStatus.status.trim() === '' && this.newStatus.files.length === 0
      )
    },
    uploadFileLimitReached() {
      return this.newStatus.files.length >= this.fileLimit
    },

    shouldAutoSaveDraft() {
      return useMergedConfigStore().mergedConfig.autoSaveDraft
    },
    debouncedMaybeAutoSaveDraft() {
      return debounce(this.maybeAutoSaveDraft, 3000)
    },
    autoSaveState() {
      if (this.saveable) {
        return this.$t('post_status.auto_save_saving')
      } else if (this.newStatus.id) {
        return this.$t('post_status.auto_save_saved')
      } else {
        return this.$t('post_status.auto_save_nothing_new')
      }
    },
    safeToSaveDraft() {
      return this.saveable && hasContent(this.newStatus)
    },

    // Error handling
    pollContentError() {
      return this.pollFormVisible && this.newStatus.poll?.error
    },

    // Featureset detection
    postFormats() {
      return useInstanceCapabilitiesStore().postFormats || []
    },
    pollsAvailable() {
      return (
        useInstanceCapabilitiesStore().pollsAvailable &&
        useInstanceStore().limits.pollLimits.max_options >= 2 &&
        this.disablePolls !== true
      )
    },
    hideExtraActions() {
      return this.disableDraft || this.hideDraft
    },

    // User configuration
    userDefaultScope() {
      return this.currentUser.default_scope
    },
    userDefaultPostContentType() {
      return this.mergedConfig.postContentType
    },
    userDefaultScopeCopy() {
      return this.mergedConfig.scopeCopy
    },
    userDefaultSensitive() {
      return this.mergedConfig.sensitiveByDefault
    },
    showAllScopes() {
      return !this.mergedConfig.minimalScopesMode
    },
    minimalScopesMode() {
      return this.mergedConfig.minimalScopesMode
    },
    submitOnEnter() {
      return this.chatView && this.mergedConfig.chatSubmitOnEnter
    },

    // Global stuff
    ...mapState(useUsersStore, ['currentUser']),
    ...mapState(useMergedConfigStore, ['mergedConfig']),
    ...mapState(useInterfaceStore, {
      mobileLayout: (state) => state.mobileLayout,
    }),
  },
  watch: {
    // the content warning field opens with CW, or by itself when the post
    // gets one (a reply to a post with a subject, a draft, an edit); it
    // stays open while its text is deleted
    'newStatus.spoilerText'(value) {
      if (value) this.subjectOpen = true
    },
    // Reply or Quote pressed while the form is open
    quoting(quote) {
      this.setQuoteMode(quote)
    },
    newStatus: {
      deep: true,
      handler() {
        this.statusChanged()
      },
    },
    saveable(val) {
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#usage_notes
      // MDN says we'd better add the beforeunload event listener only when needed, and remove it when it's no longer needed
      if (val) {
        this.addBeforeUnloadListener()
      } else {
        this.removeBeforeUnloadListener()
      }
    },
  },
  methods: {
    // the text field is a RichTextarea; its element works like a textarea
    textareaEl() {
      return this.$refs.textarea?.$el
    },
    // Composing
    changeVis(visibility) {
      this.newStatus.visibility = visibility
    },
    clearStatus() {
      this.saveInhibited = true
      this.newStatus.status = ''
      this.newStatus.spoilerText = ''
      this.subjectOpen = false
      this.newStatus.files = []
      this.newStatus.poll = null
      this.newStatus.quote = null
      this.newStatus.nsfw = this.defaultNewStatus.nsfw
      this.newStatus.mediaDescriptions = {}
      this.newStatus.scheduledAt = null
      this.newStatus.expiresIn = null
      this.$refs.mediaUpload?.clearFile()
      if (this.preserveFocus) {
        this.$nextTick(() => {
          this.textareaEl().focus()
        })
      }
      this.error = null
      if (this.preview) this.previewStatus()
      this.saveable = false
    },
    async postStatus(event) {
      // an Enter that ends an IME composition is the IME's
      if (event?.isComposing || event?.keyCode === 229) return
      if (this.posting && !this.optimisticPosting) {
        return
      }
      if (this.disableSubmit) {
        return
      }
      if (this.emojiInputShown) {
        return
      }
      if (this.submitOnEnter) {
        event.stopPropagation()
        event.preventDefault()
      }

      if (
        this.optimisticPosting &&
        (this.isEmptyStatus || this.isOverLengthLimit)
      ) {
        return
      }

      if (this.isEmptyStatus) {
        this.error = this.$t('post_status.empty_status_error')
        return
      }

      if (this.pollContentError) {
        this.error = this.pollContentError
        return
      }

      const scheduleIssue =
        this.scheduling && scheduleProblem(this.newStatus.scheduledAt)
      if (scheduleIssue) {
        this.error = this.$t(scheduleProblemKey(scheduleIssue))
        return
      }

      this.posting = true

      try {
        await this.setAllMediaDescriptions()
      } catch {
        this.error = this.$t('post_status.media_description_error')
        this.posting = false
        return
      }

      const postHandler = this.postHandler
        ? this.postHandler
        : statusPoster.postStatus

      postHandler(this.postingOptions)
        .then((data) => {
          if (data?.scheduled) {
            useInterfaceStore().pushGlobalNotice({
              level: 'success',
              messageKey: 'post_status.scheduled_notice',
              messageArgs: {
                time: formatScheduleTime(data.scheduled_at, this.$i18n?.locale),
              },
            })
          }
          this.abandonDraft()
          this.clearStatus()
          this.updateIdempotencyKey()
          this.$emit('posted', data)
        })
        .catch((error) => {
          this.error = error
          // the server answered, and keeps that answer for the key: the
          // retry needs a new one. No answer (the network): the post may
          // have arrived, the key stays so a retry cannot post twice.
          if (error?.name === 'StatusCodeError') this.updateIdempotencyKey()
        })
        .finally(() => {
          this.posting = false
        })
    },

    // Preview
    previewStatus() {
      if (this.isEmptyStatus && this.newStatus.spoilerText.trim() === '') {
        this.preview = { error: this.$t('post_status.preview_empty') }
        this.previewLoading = false
        return
      }
      this.previewLoading = true

      statusPoster
        .postStatus(this.previewOptions)
        .then((data) => {
          // Don't apply preview if not loading, because it means
          // user has closed the preview manually.
          if (!this.previewLoading) return
          this.preview = data
        })
        .catch((error) => {
          this.preview = { error }
        })
        .finally(() => {
          this.previewLoading = false
          // If we post right after doing a preview request
          // backend treats non-preview request as preview one
          // and "eats" the post
          this.updateIdempotencyKey()
        })
    },
    debouncePreviewStatus: debounce(function () {
      this.previewStatus()
    }, 500),
    autoPreview() {
      if (!this.preview) return
      this.previewLoading = true
      this.debouncePreviewStatus()
    },
    closePreview() {
      this.preview = null
      this.previewLoading = false
    },
    togglePreview() {
      if (this.showPreview) {
        this.closePreview()
      } else {
        this.previewStatus()
      }
    },

    // Attachments
    setMediaDescription(id) {
      const description = this.newStatus.mediaDescriptions[id]
      if (!description || description.trim() === '') return
      return statusPoster.setMediaDescription({
        id,
        description,
      })
    },
    setAllMediaDescriptions() {
      const ids = this.newStatus.files.map((file) => file.id)
      return Promise.all(ids.map((id) => this.setMediaDescription(id)))
    },
    addMediaFile(fileInfo) {
      this.newStatus.files.push(fileInfo)
    },
    uploadFailed(errString, templateArgs) {
      templateArgs = templateArgs || {}
      this.error =
        this.$t('upload.error.base') +
        ' ' +
        this.$t('upload.error.' + errString, templateArgs)
    },
    startedUploadingFiles() {
      this.uploadingFiles = true
    },
    finishedUploadingFiles() {
      this.uploadingFiles = false
    },
    paste(e) {
      this.autoPreview()
      if (e.clipboardData.files.length > 0) {
        // prevent pasting of file as text
        e.preventDefault()
        // Strangely, files property gets emptied after event propagation
        // Trying to wrap it in array doesn't work. Plus I doubt it's possible
        // to hold more than one file in clipboard.
        this.dropFiles = [e.clipboardData.files[0]]
      }
    },
    // keep Post in view while typing at the end of the text
    keepBottomInView() {
      const el = this.textareaEl()
      if (el.selectionStart !== el.value.length) return
      this.$nextTick(() =>
        this.$refs.bottom?.$el.scrollIntoView({ block: 'nearest' }),
      )
    },

    // Poll
    togglePollForm() {
      this.newStatus.poll = this.hasPoll ? null : {}
    },

    // Quote
    // a quote of the replied-to post, or a reply to it. What was typed
    // stays; the reply mention and subject go, or come back. Only the whole
    // mention prefix is taken off: edited mentions stay as typed, and a
    // reply gets the full prefix again in front of them.
    setQuoteMode(quote) {
      const mentions = this.mentionsString
      const subject = this.repliedSubjectString ?? ''
      const text = this.newStatus.status ?? ''
      if (quote !== !!this.quoteThreadToggled) this.quoteThreadToggled = quote
      if (quote) {
        if (mentions && text.startsWith(mentions)) {
          this.newStatus.status = text.slice(mentions.length)
        }
        if (subject && this.newStatus.spoilerText === subject) {
          this.newStatus.spoilerText = ''
        }
      } else {
        if (!text.startsWith(mentions)) this.newStatus.status = mentions + text
        if (!this.newStatus.spoilerText) this.newStatus.spoilerText = subject
      }
    },
    // Drafts
    statusChanged() {
      this.autoPreview()
      this.updateIdempotencyKey()
      this.debouncedMaybeAutoSaveDraft()
      this.saveable = true
      this.saveInhibited = false
    },
    async saveDraft() {
      const step = draftStep({
        disabled: this.disableDraft,
        inhibited: this.saveInhibited,
        saveable: this.saveable,
        newStatus: this.newStatus,
      })
      if (step === 'none') return
      if (step === 'save') {
        this.newStatus.id = await useDraftsStore().addOrSaveDraft({
          type: this.statusType,
          refId: this.refId,
          ...this.newStatus,
          // Draft ID overwrites status ID (which is undefined for fresh statuses)
          id: this.draftId,
        })
      } else {
        await this.abandonDraft()
      }
      this.saveable = false
      if (!this.shouldAutoSaveDraft) {
        this.clearStatus()
        this.updateIdempotencyKey()
        this.$emit('draft-done')
      }
    },
    maybeAutoSaveDraft() {
      if (this.shouldAutoSaveDraft) {
        this.saveDraft()
      }
    },
    abandonDraft() {
      return useDraftsStore().abandonDraft(this.draftId)
    },
    requestClose() {
      if (!this.saveable) {
        this.$emit('close-accepted')
      } else {
        this.$refs.draftCloser.requestClose()
      }
    },
    saveAndCloseDraft() {
      this.saveDraft().then(() => {
        this.$emit('close-accepted')
      })
    },
    discardAndCloseDraft() {
      this.abandonDraft().then(() => {
        this.$emit('close-accepted')
      })
    },
    addBeforeUnloadListener() {
      this._beforeUnloadListener ||= () => {
        this.saveDraft()
      }
      window.addEventListener('beforeunload', this._beforeUnloadListener)
    },
    removeBeforeUnloadListener() {
      if (this._beforeUnloadListener) {
        window.removeEventListener('beforeunload', this._beforeUnloadListener)
      }
    },

    // Misc
    propsToNative(props) {
      return propsToNative(props)
    },
    handleEmojiInputShow(value) {
      this.emojiInputShown = value
    },
    // unique, also for two made in the same millisecond
    updateIdempotencyKey() {
      this.idempotencyKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    },
    toggleSubject() {
      if (this.showSubject) {
        // a closed field must not post a hidden warning
        this.subjectOpen = false
        this.newStatus.spoilerText = ''
        this.textareaEl().focus()
        return
      }
      this.subjectOpen = true
      this.$nextTick(() =>
        focusAtEnd(
          this.$refs['subject-input']?.$el.querySelector('.form-post-subject'),
        ),
      )
    },
    setLanguage(code) {
      this.newStatus.language = code
    },
    toggleExpiry() {
      this.newStatus.expiresIn =
        this.newStatus.expiresIn === null ? DEFAULT_EXPIRY : null
    },
    toggleSchedule() {
      this.newStatus.scheduledAt =
        this.newStatus.scheduledAt === null ? defaultSchedule() : null
    },
    toggleEmojiPicker() {
      const input = this.$refs['emoji-input']
      if (input.pickerShown) {
        input.$refs.picker.hidePicker()
      } else {
        this.textareaEl().focus()
        input.triggerShowPicker()
      }
    },
    clearError() {
      this.error = null
    },
    ...mapActions(useMediaViewerStore, ['increment']),
  },
  beforeUnmount() {
    this.resizeObserver.disconnect()
    this.maybeAutoSaveDraft()
    this.removeBeforeUnloadListener()
  },
}

export default PostStatusForm
